import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Icon, Badge, SectionLabel, ModuleDot, IconButton } from "../ui";
import { listProjects, dotLabel, ProjectSummary } from "../api/projects";
import { sourceToToolPath, SourceKind } from "../components/SourcePicker";

/* Landing — project-first dashboard, ported from oab-v1-design/components/landing.jsx.
 * Renders as content under the global AppShell chrome (no own top bar).
 * Backed by GET /api/project/list; sample-only fields (miles, status, module
 * breakdown) are dropped or derived since /list doesn't carry them yet. */

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
}

// Deterministic alignment-over-terrain SVG thumbnail, keyed off project id.
const ProjectThumb: React.FC<{ project: ProjectSummary; large?: boolean }> = ({ project, large = false }) => {
  const seed = project.project_id.split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  const w = large ? 720 : 280;
  const h = large ? 360 : 180;
  const segs = 6;
  const points: [number, number][] = [];
  for (let i = 0; i <= segs; i++) {
    const t = i / segs;
    const x = 20 + t * (w - 40);
    const y = h * 0.5 + Math.sin(seed + t * 6) * h * 0.25 + Math.cos(seed * 0.7 + t * 3) * h * 0.1;
    points.push([x, y]);
  }
  const path = points.map((p, i) => (i ? "L" : "M") + p[0].toFixed(1) + " " + p[1].toFixed(1)).join(" ");

  const moduleEntries = Object.entries(project.asset_counts || {});
  const dots: { x: number; y: number; mod: string }[] = [];
  if (moduleEntries.length) {
    const n = large ? 26 : 12;
    for (let i = 0; i < n; i++) {
      const t = (i + 0.5) / n;
      const idx = Math.floor((Math.sin(seed * 1.3 + i * 2.1) + 1) * moduleEntries.length / 2) % moduleEntries.length;
      const mod = moduleEntries[idx]?.[0] || "light";
      const px = 20 + t * (w - 40);
      const py = h * 0.5 + Math.sin(seed + t * 6) * h * 0.25 + Math.cos(seed * 0.7 + t * 3) * h * 0.1;
      const off = Math.sin(seed * 2.1 + i * 1.7) * 14;
      dots.push({ x: px, y: py + off, mod });
    }
  }
  const contours: string[] = [];
  const cN = large ? 6 : 4;
  for (let c = 0; c < cN; c++) {
    const baseY = (h / (large ? 7 : 5)) * (c + 1);
    let d = `M 0 ${baseY}`;
    for (let i = 1; i <= 14; i++) {
      const x = (i / 14) * w;
      const y = baseY + Math.sin(seed * 0.5 + c + i * 0.6) * 8;
      d += ` L ${x.toFixed(1)} ${y.toFixed(1)}`;
    }
    contours.push(d);
  }
  const modColor: Record<string, string> = {
    light_pole: "var(--mod-light)", light: "var(--mod-light)",
    pavement_markings: "var(--mod-mark)", fencing: "var(--mod-fence)",
    drainage: "var(--mod-drain)", ground_sign: "var(--mod-sign)",
    overhead_sign: "var(--mod-sign)", sound_wall: "var(--mod-wall)",
    guardrail: "var(--mod-rail)", traffic_signal: "var(--mod-signal)",
  };
  return (
    <div style={{ position: "relative", width: "100%", height: "100%", background: "var(--bg-canvas)", overflow: "hidden", borderRadius: large ? "var(--r-lg)" : "var(--r-md)" }}>
      <svg width="100%" height="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="xMidYMid slice">
        {contours.map((d, i) => (
          <path key={i} d={d} stroke="var(--border-default)" strokeWidth="0.6" fill="none" opacity={0.4 + i * 0.05} />
        ))}
        <defs>
          <pattern id={`grid-${project.project_id}`} width={large ? 40 : 24} height={large ? 40 : 24} patternUnits="userSpaceOnUse">
            <path d={`M ${large ? 40 : 24} 0 L 0 0 0 ${large ? 40 : 24}`} fill="none" stroke="var(--border-subtle)" strokeWidth="0.5" />
          </pattern>
        </defs>
        <rect width={w} height={h} fill={`url(#grid-${project.project_id})`} opacity="0.5" />
        <path d={path} stroke="var(--fg-primary)" strokeWidth={large ? 4 : 3} fill="none" opacity="0.08" strokeLinecap="round" />
        <path d={path} stroke="var(--accent)" strokeWidth={large ? 2 : 1.5} fill="none" strokeLinecap="round" />
        {dots.map((d, i) => (
          <circle key={i} cx={d.x} cy={d.y} r={large ? 3 : 2} fill={modColor[d.mod] || "var(--fg-muted)"} />
        ))}
      </svg>
      <div className="mono" style={{ position: "absolute", top: 8, right: 8, width: 22, height: 22, borderRadius: "50%", background: "var(--bg-surface)", border: "1px solid var(--border-subtle)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 600, color: "var(--fg-secondary)" }}>N</div>
    </div>
  );
};

const HeroCard: React.FC<{ project: ProjectSummary; onOpen: () => void }> = ({ project, onOpen }) => (
  <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", background: "var(--bg-surface)", border: "1px solid var(--border-subtle)", borderRadius: "var(--r-xl)", overflow: "hidden", boxShadow: "var(--shadow-2)" }}>
    <div style={{ aspectRatio: "16/9", borderRight: "1px solid var(--border-subtle)" }}>
      <ProjectThumb project={project} large />
    </div>
    <div style={{ padding: "28px 32px", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <SectionLabel>Continue working</SectionLabel>
          <span style={{ flex: 1 }} />
          <Badge tone="neutral" mono>{dotLabel(project.dot)}</Badge>
        </div>
        <h1 style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.02em", margin: "0 0 8px", lineHeight: 1.15 }}>{project.name}</h1>
        <div className="mono" style={{ fontSize: 12, color: "var(--fg-tertiary)", marginBottom: 20 }}>
          <span className="tab-num">{project.total_assets.toLocaleString()}</span> assets
          <span style={{ opacity: 0.4, margin: "0 8px" }}>·</span>
          EPSG:{project.crs_epsg}
          {project.has_corridor && <><span style={{ opacity: 0.4, margin: "0 8px" }}>·</span>corridor</>}
          {project.has_alignment && <><span style={{ opacity: 0.4, margin: "0 8px" }}>·</span>alignment</>}
        </div>
        <div style={{ padding: "10px 12px", background: "var(--bg-surface-2)", borderRadius: "var(--r-sm)", fontSize: 12.5, color: "var(--fg-secondary)", display: "flex", alignItems: "center", gap: 8 }}>
          <Icon name="history" size={14} color="var(--fg-tertiary)" />
          <span>Created {fmtDate(project.created)}</span>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 24 }}>
        <Button variant="primary" size="lg" iconRight="chevRight" onClick={onOpen}>Resume project</Button>
        <div style={{ flex: 1 }} />
        <IconButton icon="moreH" title="Project actions" />
      </div>
    </div>
  </div>
);

const GridCard: React.FC<{ project: ProjectSummary; onOpen: () => void }> = ({ project, onOpen }) => (
  <button
    onClick={onOpen}
    style={{ background: "var(--bg-surface)", border: "1px solid var(--border-subtle)", borderRadius: "var(--r-lg)", overflow: "hidden", transition: "transform 160ms, box-shadow 160ms, border-color 160ms", cursor: "pointer", display: "flex", flexDirection: "column", textAlign: "left", padding: 0 }}
    onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "var(--shadow-3)"; e.currentTarget.style.borderColor = "var(--border-default)"; }}
    onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; e.currentTarget.style.borderColor = "var(--border-subtle)"; }}
  >
    <div style={{ aspectRatio: "5/3", borderBottom: "1px solid var(--border-subtle)", width: "100%" }}>
      <ProjectThumb project={project} />
    </div>
    <div style={{ padding: "12px 14px 14px", display: "flex", flexDirection: "column", gap: 6, width: "100%" }}>
      <h3 style={{ fontSize: 14, fontWeight: 600, margin: 0, letterSpacing: "-0.012em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{project.name}</h3>
      <div className="mono" style={{ fontSize: 11, color: "var(--fg-tertiary)" }}>
        {dotLabel(project.dot)} <span style={{ opacity: 0.4 }}>·</span> EPSG:{project.crs_epsg} <span style={{ opacity: 0.4 }}>·</span> {fmtDate(project.created)}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 4, minHeight: 14 }}>
        {project.total_assets > 0 ? (
          <span className="mono tab-num" style={{ fontSize: 11, color: "var(--fg-secondary)" }}>{project.total_assets.toLocaleString()} assets</span>
        ) : (
          <span style={{ fontSize: 11, color: "var(--fg-muted)", fontStyle: "italic" }}>No assets yet</span>
        )}
      </div>
    </div>
  </button>
);

interface SourceDef { kind: SourceKind; icon: string; title: string; subtitle: string; group?: string; dim?: boolean; }
const SourceTile: React.FC<{ s: SourceDef; onClick: () => void }> = ({ s, onClick }) => (
  <button
    onClick={onClick}
    style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 8, padding: "16px 16px 18px", background: "var(--bg-surface)", border: "1px solid var(--border-subtle)", borderRadius: "var(--r-lg)", textAlign: "left", transition: "all 140ms", opacity: s.dim ? 0.6 : 1, position: "relative", overflow: "hidden" }}
    onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--accent-border)"; e.currentTarget.style.background = "var(--accent-soft)"; }}
    onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--border-subtle)"; e.currentTarget.style.background = "var(--bg-surface)"; }}
  >
    <div style={{ width: 32, height: 32, borderRadius: "var(--r-sm)", background: "var(--bg-surface-2)", color: "var(--fg-primary)", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Icon name={s.icon} size={18} />
    </div>
    <div>
      <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--fg-primary)", marginBottom: 2, letterSpacing: "-0.01em" }}>{s.title}</div>
      <div style={{ fontSize: 11.5, color: "var(--fg-tertiary)", lineHeight: 1.4 }}>{s.subtitle}</div>
    </div>
    {s.group && <span className="mono" style={{ position: "absolute", top: 12, right: 12, fontSize: 9, color: "var(--fg-muted)", letterSpacing: "0.06em", textTransform: "uppercase" }}>{s.group}</span>}
  </button>
);

const ASSET_SOURCES: SourceDef[] = [
  { kind: "xlsx", icon: "spreadsheet", title: "From Spreadsheet", subtitle: "Bulk-generate assets via Excel or CSV", group: "Asset Builder" },
  { kind: "gis", icon: "map", title: "From GIS", subtitle: "Draw a polygon over a public REST source", group: "GIS Import" },
  { kind: "dxf", icon: "cad", title: "From DXF / CAD", subtitle: "Layer classification, then place into project", group: "CAD-to-BIM" },
  { kind: "pdf", icon: "pdf", title: "From PDF plans", subtitle: "Calibrate sheets, then place assets", group: "PDF Staging" },
  { kind: "pavement", icon: "layers", title: "From Pavement", subtitle: "Footprint x design x surface to courses", group: "Pavement" },
];

export default function Landing() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ProjectSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // This deployment has no login, so OAB can't know who's visiting. Distinguish
  // a first-ever visit (this browser) from a return visit via a localStorage
  // flag, and greet generically — no personal name. Read-then-set once on mount.
  const [isFirstVisit] = useState<boolean>(() => {
    try {
      const KEY = "oab.hasVisited";
      if (!localStorage.getItem(KEY)) {
        localStorage.setItem(KEY, "1");
        return true;
      }
    } catch {
      // localStorage unavailable (private mode / blocked) — treat as a return visit.
    }
    return false;
  });

  useEffect(() => {
    let alive = true;
    listProjects()
      .then((p) => { if (alive) setProjects(p); })
      .catch((e) => { if (alive) setError(String(e.message || e)); });
    return () => { alive = false; };
  }, []);

  const openProject = (p: ProjectSummary) => navigate(`/p/${p.project_id}`, { state: { project: p } });

  const hero = projects?.[0];
  const recent = projects?.slice(1) ?? [];

  return (
    <div style={{ background: "var(--bg-app)", minHeight: "calc(100vh - 56px)", paddingBottom: 80 }}>
      <main style={{ maxWidth: 1240, margin: "0 auto", padding: "40px 32px 0" }}>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 24, gap: 24 }}>
          <div>
            <div className="mono" style={{ fontSize: 11, color: "var(--fg-tertiary)", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>
              {new Date().toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
            </div>
            <h1 style={{ fontSize: 32, fontWeight: 600, letterSpacing: "-0.025em", margin: 0, lineHeight: 1.1 }}>{isFirstVisit ? "Welcome to OpenAssetBuilder." : "Welcome back."}</h1>
          </div>
          {projects && (
            <div className="mono" style={{ fontSize: 11, color: "var(--fg-muted)", letterSpacing: "0.04em", textTransform: "uppercase", textAlign: "right" }}>
              <div><span className="tab-num">{projects.length}</span> projects · <span className="tab-num">{projects.reduce((a, p) => a + p.total_assets, 0).toLocaleString()}</span> assets</div>
              <div style={{ marginTop: 2 }}>schema IFC4X3</div>
            </div>
          )}
        </div>

        {error && (
          <div style={{ padding: "14px 16px", borderRadius: "var(--r-md)", background: "var(--status-err-bg)", color: "var(--status-err)", border: "1px solid var(--border-subtle)", fontSize: 13 }}>
            Couldn't reach the backend: {error}. Is the API running on :8000?
          </div>
        )}

        {!projects && !error && (
          <div style={{ padding: 40, textAlign: "center", color: "var(--fg-muted)", fontSize: 13 }}>Loading projects…</div>
        )}

        {hero && <HeroCard project={hero} onOpen={() => openProject(hero)} />}

        {recent.length > 0 && (
          <section style={{ marginTop: 56 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
              <SectionLabel>Recent projects</SectionLabel>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
              {recent.map((p) => <GridCard key={p.project_id} project={p} onOpen={() => openProject(p)} />)}
            </div>
          </section>
        )}

        <section style={{ marginTop: 56 }}>
          <div style={{ marginBottom: 6 }}>
            <SectionLabel>Start a new project</SectionLabel>
          </div>
          <p style={{ fontSize: 13, color: "var(--fg-secondary)", margin: "4px 0 18px" }}>
            Every project is a set of <em style={{ color: "var(--fg-primary)", fontStyle: "normal", fontWeight: 500 }}>sources</em> plus the assets generated against them. Pick one to begin.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12 }}>
            {ASSET_SOURCES.map((s) => (
              <SourceTile key={s.title} s={s} onClick={() => navigate(sourceToToolPath(s.kind) || "/")} />
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
